import {
    DS_BREAKPOINT,
    DS_COLOR,
    DS_HEIGHT,
    DS_SHAPE_BORDER_RADIUS,
    DS_SPACING_UNITS,
} from '@calm/evexia-design-tokens';
import { CardContainer, CardProps } from '@rhg/components/card';
import { MaxColumnsContainer } from '@rhg/components/ui/layout';
import { useCurrentBreakpointName } from '@rhg/hooks/use-current-breakpoint-name';
import { useProvidedTheme } from '@rhg/hooks/use-provided-theme';
import {
    Box,
    Stack,
    SxProps,
    Theme,
    ThemeProvider,
    useTheme,
} from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { useMemo, useState } from 'react';
import CardContent from '../card/card-content';
import CardImage from '../card/card-image';
import { ProgramBannerImage } from '../program/banner-image';
import {
    BANNER_HEIGHT,
    BANNER_IMAGE_BLUR_PX,
    BANNER_IMAGE_HEIGHT_MD_PX,
} from '../program/banner-image/types';
import { BANNER_V2_HEIGHT } from './types';

interface BannerCardProps extends Omit<CardProps, 'imageAlt'> {
    buttonGroupSx?: SxProps<Theme>;
    skipHorizontalContentAdjustments?: boolean;
}

const styleActualGridWrapper = {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    position: 'relative',
    backgroundColor: (theme: Theme) => ({
        xs: 'unset',
        md:
            theme.palette.mode === 'light'
                ? DS_COLOR.WHITE_100P
                : DS_COLOR.LIGHT_TEXT_BODY, // LIGHT_TEXT_BODY matches dark linear gradient color}
    }),
};
const styleContentWrapper = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flexGrow: 1,
};
const styleContent = {
    position: 'relative',
    maxWidth: DS_BREAKPOINT.MEDIUM,
    width: '100%',
    flex: 1,
    px: {
        sm: DS_SPACING_UNITS.SIDE_SMALL,
        md: DS_SPACING_UNITS.SIDE_MEDIUM,
        lg: DS_SPACING_UNITS.SIDE_LARGE,
    },
};
const styleCardContent = {
    zIndex: 2,
};
const styleHorizontalContainer = {
    maxHeight: DS_HEIGHT.HORIZONTAL_CARD_SIZE,
    display: 'flex',
    flexDirection: 'row-reverse',
    flexWrap: 'nowrap',
};

const styleBannerImage = {
    maskImage: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? 'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 25%)'
            : 'linear-gradient(90deg, rgba(26, 26, 44, 0) 0%, #1A1A2C 25%)',
    width: '100%',
};
const styleBackground = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: {
        ...BANNER_HEIGHT,
    },
};

const styleMaxImageHeight = {
    maxHeight: BANNER_V2_HEIGHT,
};
const styleBannerImageContainer = {
    overflow: 'initial',
};
const stylyBackgroundBannerImage = {
    height: {
        ...BANNER_HEIGHT,
        md: `${BANNER_IMAGE_HEIGHT_MD_PX + BANNER_IMAGE_BLUR_PX * 2}px`,
    },
    filter: {
        xs: undefined,
        sm: undefined,
        md: `blur(${BANNER_IMAGE_BLUR_PX}px)`,
    },
    maskImage: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? 'linear-gradient(90deg, #FFFFFF 49%, rgba(255, 255, 255, 0) 70%)'
            : 'linear-gradient(90deg, #1A1A2C 49%, rgba(26, 26, 44, 0) 70%)',
    '::before': {
        backgroundColor: (theme: Theme) =>
            theme.palette.mode === 'light'
                ? theme.palette.mask.white['80p']
                : theme.palette.mask.black['60p'],
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
    },
};
const styleHorizontalImage = {
    width: DS_HEIGHT.HORIZONTAL_CARD_SIZE,
};
const styleCardContainer = {
    width: '100%',
};
const styleHorizontalContent = {
    alignItems: 'center',
    '& > div:first-of-type': {
        alignSelf: 'start',
        width: '100%',
    },
};

export const BannerCard = ({
    title,
    titleMaxLines,
    titleComponent,
    titleTopSpacing,
    titleBottomSpacing,
    subtitle,
    subtitleVariant,
    subtitleMaxLines,
    titleIcon,
    contentTag,
    contentAction,
    categoryTitle,
    categoryTitleColor,
    categoryIcon,
    categoryDescription,
    progress,
    progressText,
    buttonGroup,
    buttonGroupSx,
    backgroundColor,
    imageFocusIcon,
    imageSrc,
    imageIconTop,
    imageIconBottom,
    imageForcedSize = 400,
    imageShowIconsOnFocused,
    isEntireCardClickable,
    entireCardButtonProps,
    onClick,
    label,
    trackButton,
    sx,
    CardImageProps,
    contentSx,
    buttonTopSpacing,
    additionalAction,
    isNotClickable,
    role,
    skipHorizontalContentAdjustments = false,
    'aria-label': ariaLabel,
    'data-syntheticid': dataSyntheticId,
}: BannerCardProps) => {
    const currentBreakpoint = useCurrentBreakpointName();
    const theme = useTheme();
    const lightTheme = useProvidedTheme('light');
    const darkTheme = useProvidedTheme('dark');
    const [hasFocus, setHasFocus] = useState(false);

    const clickHandlers = useMemo(
        () => ({
            onMouseEnter: () => setHasFocus(true),
            onMouseLeave: () => setHasFocus(false),
            onFocus: () => setHasFocus(true),
            onBlur: () => setHasFocus(false),
            onClick: onClick,
        }),
        [onClick]
    );

    const show3by2Ratio = ['xl', 'lg'].includes(currentBreakpoint);

    // TODO: switch this to be based on average image color
    // ref: https://codepen.io/AtomicForce/pen/nVgamy
    const isDarkImageGradiant = theme.palette.mode === 'dark';
    const showAsHorizontal = ['sm', 'xs'].includes(currentBreakpoint);
    const styleForcedSize = imageForcedSize
        ? { width: imageForcedSize }
        : undefined;

    if (showAsHorizontal) {
        return (
            <Stack gap={4} alignItems="center" sx={{ width: '100%' }}>
                <CardContainer
                    clickHandlers={clickHandlers}
                    role={role}
                    ariaLabel={ariaLabel}
                    isNotClickable={isNotClickable}
                    entireCardButtonProps={entireCardButtonProps}
                    isEntireCardClickable={isEntireCardClickable}
                    sx={sxAppend(styleCardContainer, sx)}
                    backgroundColor={backgroundColor}
                    trackButton={trackButton}
                    data-cy="bannerCard"
                    data-syntheticid={dataSyntheticId}
                >
                    <Box
                        sx={sxAppend(
                            styleActualGridWrapper,
                            styleHorizontalContainer
                        )}
                        style={{ backgroundColor: backgroundColor }}
                    >
                        <Box sx={styleContentWrapper}>
                            <ThemeProvider
                                theme={
                                    isDarkImageGradiant ? darkTheme : lightTheme
                                }
                            >
                                <CardContent
                                    title={title}
                                    titleComponent={titleComponent}
                                    titleMaxLines={titleMaxLines ?? 2}
                                    titleBottomSpacing={titleBottomSpacing}
                                    subtitle={subtitle}
                                    subtitleVariant={subtitleVariant}
                                    subtitleMaxLines={subtitleMaxLines}
                                    tag={contentTag}
                                    action={contentAction}
                                    categoryTitleColor={categoryTitleColor}
                                    progress={progress}
                                    progressText={progressText}
                                    sx={sxAppend(
                                        contentSx,
                                        additionalAction
                                            ? styleHorizontalContent
                                            : {}
                                    )}
                                    additionalAction={additionalAction}
                                    buttonTopSpacing={buttonTopSpacing}
                                    categoryTitle={categoryTitle}
                                    categoryIcon={categoryIcon}
                                    titleTopSpacing={0}
                                    variant="horizontal"
                                    skipHorizontalContentAdjustments={
                                        skipHorizontalContentAdjustments
                                    }
                                />
                            </ThemeProvider>
                        </Box>
                        <Box>
                            <CardImage
                                src={imageSrc}
                                alt=""
                                ratio={show3by2Ratio ? '3:2' : '1:1'}
                                sx={sxAppend(
                                    styleForcedSize,
                                    styleHorizontalImage
                                )}
                                bezel={DS_SHAPE_BORDER_RADIUS.CARD}
                                imageSideRadiusEnabled={false}
                                iconTop={imageIconTop}
                                iconBottom={imageIconBottom}
                                iconFocus={imageFocusIcon}
                                isFocused={hasFocus}
                                showIconsOnFocused={imageShowIconsOnFocused}
                                aria-label={
                                    onClick && label ? label : undefined
                                }
                                trackButton={trackButton}
                                {...CardImageProps}
                                {...(isNotClickable || isEntireCardClickable
                                    ? {}
                                    : clickHandlers)}
                            />
                        </Box>
                    </Box>
                </CardContainer>
                {buttonGroup}
            </Stack>
        );
    }

    return (
        <MaxColumnsContainer columns={12}>
            <CardContainer
                clickHandlers={clickHandlers}
                role={role}
                ariaLabel={ariaLabel}
                isNotClickable={isNotClickable}
                entireCardButtonProps={entireCardButtonProps}
                isEntireCardClickable={isEntireCardClickable}
                sx={sxAppend(styleCardContainer, sx)}
                backgroundColor={backgroundColor}
                trackButton={trackButton}
                data-cy="bannerCard"
                data-syntheticid={dataSyntheticId}
            >
                <Box
                    sx={styleActualGridWrapper}
                    style={{ backgroundColor: backgroundColor }}
                >
                    <Box sx={sxAppend(styleContentWrapper, styleContent)}>
                        <ThemeProvider
                            theme={isDarkImageGradiant ? darkTheme : lightTheme}
                        >
                            <CardContent
                                title={title}
                                titleComponent={titleComponent}
                                titleMaxLines={titleMaxLines ?? 2}
                                titleBottomSpacing={titleBottomSpacing}
                                subtitle={subtitle}
                                subtitleVariant={subtitleVariant}
                                subtitleMaxLines={subtitleMaxLines}
                                tag={contentTag}
                                action={contentAction}
                                categoryTitleColor={categoryTitleColor}
                                progress={progress}
                                progressText={progressText}
                                sx={sxAppend(styleCardContent, contentSx)}
                                additionalAction={additionalAction}
                                buttonTopSpacing={buttonTopSpacing}
                                buttonGroup={buttonGroup}
                                variant="bannerV2"
                                icon={titleIcon}
                                titleTopSpacing={titleTopSpacing}
                                categoryTitle={categoryTitle}
                                categoryDescription={categoryDescription}
                                categoryIcon={categoryIcon}
                                buttonGroupSx={buttonGroupSx}
                            />
                        </ThemeProvider>
                    </Box>
                    <Box>
                        <ProgramBannerImage
                            programImageSrc={imageSrc}
                            sxBannerImage={sxAppend(styleBannerImage, {
                                aspectRatio: show3by2Ratio ? `${3 / 2}` : 1,
                            })}
                            sx={styleMaxImageHeight}
                        />
                    </Box>
                    <ProgramBannerImage
                        sx={sxAppend(styleBackground, styleMaxImageHeight)}
                        programImageSrc={imageSrc}
                        sxBannerImage={stylyBackgroundBannerImage}
                        sxBannerImageContainer={styleBannerImageContainer}
                        role="presentation"
                    />
                </Box>
            </CardContainer>
        </MaxColumnsContainer>
    );
};
